// Copied from views/AccountSettingsView.vue to client/overview for separation of concerns
<script setup lang="ts">
import { useAccountStore } from '@/stores/accountStore'
import { useRoute, useRouter } from 'vue-router'
import { ref, onMounted, computed, watch } from 'vue'
import {
  fetchAccountSingle,
  fetchAccountsProduct,
  fetchAccountProductsClient,
  toggleProductForAccount,
  VerifyUsers,
  toggleAccountStatus as toggleAccountStatusClient,
} from '@/api/accountsClient'
import { getParentChild } from '@/api/accountsAdmin'
import ProgressLoader from '@/components/ProgressLoader.vue'
import { useUserStore } from '@/stores/userStore'

const { role } = useUserStore()
const store = useAccountStore()
const route = useRoute()
const accountName = String(route.params.name)
const accountData = ref<any>(null)
const isLoading = ref(true)
const accountProducts = ref<any[]>([])
const selectedAccount = ref<{ accountName: string; productName: string; enabled?: boolean } | null>(
  null
)
const dialog = ref(false)
const loadingProd = ref(false)
const productsLoaded = ref(false)

const accountNameSend = ref('')
const productNameSend = ref('')
const newStatus = ref(false)
const userData = useUserStore()

const router = useRouter()

// Helpers to match admin view
const dash = (v: any) => (v === null || v === undefined || v === '' ? '—' : String(v))
const asLink = (url?: string) => {
  if (!url) return undefined
  try {
    const u = new URL(String(url).startsWith('http') ? String(url) : `https://${url}`)
    return u.toString()
  } catch {
    return undefined
  }
}
const telHref = (phone?: string) => {
  if (!phone) return undefined
  const cleaned = String(phone).replace(/[^0-9+]/g, '')
  return cleaned ? `tel:${cleaned}` : undefined
}
const copyToClipboard = async (text?: string) => {
  if (!text) return
  try {
    await navigator.clipboard.writeText(text)
  } catch {}
}

// Section visibility driven by hash
const showInfoSection = computed(() => route.hash !== '#products')
const showProductsSection = computed(() => route.hash === '#products')

const snackbar = ref(false)
const snackbarText = ref('')
const snackbarColor = ref<'success' | 'error' | 'info' | 'warning'>('success')
const showSnack = (text: string, color: 'success' | 'error' | 'info' | 'warning' = 'success') => {
  snackbarText.value = text
  snackbarColor.value = color
  snackbar.value = true
}

const toggleAccountFunc = async () => {
  try {
    if (!selectedAccount.value) return
    const toggleData = selectedAccount.value
    const acct = String(toggleData.accountName)
    const productName = String(toggleData.productName)
    const enabling = Boolean(toggleData.enabled)

    // Backend now handles recursive child disabling automatically
    await toggleProductForAccount(acct, productName)

    // Update local state
    const idx = accountProducts.value.findIndex((p: any) => p.productName === productName)
    if (idx !== -1) {
      ;(accountProducts.value[idx] as any).isActive = enabling
      accountProducts.value = accountProducts.value.slice()
    }

    showSnack(`Product ${enabling ? 'enabled' : 'disabled'} successfully`, 'success')
  } catch (error) {
    console.error('Error toggling product:', error)
    showSnack('Failed to toggle product', 'error')
  } finally {
    dialog.value = false
    try {
      store.setProductsCacheForAccount(accountName, accountProducts.value.slice())
    } catch {}
  }
}

const confirmToggleAccountStatus = (productName: string, isActive: boolean) => {
  selectedAccount.value = {
    accountName,
    productName,
    enabled: !isActive, // Toggle the value
  }
  dialog.value = true
}

const loadAccounts = async () => {
  isLoading.value = true
  try {
    const data = await fetchAccountSingle(accountName)
    console.log(data)
    accountData.value = data
    store.selectedAccountData = data
  } catch (error) {
    console.error('Failed to load account:', error)
  } finally {
    isLoading.value = false
  }
}

const loadAccountProducts = async () => {
  loadingProd.value = true
  try {
    // use cached data for this account if available
    const cached = store.getProductsCacheForAccount(accountName)
    if (cached && cached.length) {
      accountProducts.value = cached.slice()
      loadingProd.value = false
      return
    }
    loadingProd.value = true
    const data = await fetchAccountsProduct(accountName)

    // For client view, display all available products for this account,
    // but respect the showToCustomers flag
    accountProducts.value = Array.isArray(data) ? (data as any[]) : []
    accountProducts.value = accountProducts.value.filter(
      (product: any) => product.showToCustomers !== false
    )
    // cache for this account for subsequent navigations
    try {
      store.setProductsCacheForAccount(accountName, accountProducts.value.slice())
    } catch {}

    console.log('final obj', accountProducts.value)
    loadingProd.value = false
  } catch (error) {
    console.error('Failed to load accounts:', error)
    loadingProd.value = false
  }
}

const headers = [
  { title: 'Product Friendly Name', value: 'productFriendlyName', sortable: true },
  { title: 'Status', value: 'status', sortable: false },
  { title: 'Actions', value: 'actions', sortable: false, align: 'end' },
]
const headers1 = [{ title: 'Product Name', value: 'productFriendlyName', sortable: true }]

onMounted(() => {
  VerifyUsers()
  loadAccounts()
  if (route.hash === '#products' && !productsLoaded.value) {
    productsLoaded.value = true
    loadAccountProducts()
  }
})

watch(
  () => route.hash,
  (h) => {
    if (h === '#products' && !productsLoaded.value) {
      productsLoaded.value = true
      loadAccountProducts()
    }
  }
)

const navigateToEditAccount = (account: string) => {
  router.push(`/account/edit/${account}`)
}
const addProductForAccount = (account: string) => {
  router.push(`/account/products/${account}`)
}

function goCreateUser(item: any) {
  try {
    localStorage.setItem('accountNameFriendly', item?.name || '')
  } catch {}
  router.push({
    path: '/users/create-user',
    query: { juid: String(item?.juid || ''), 'create-client-admin': 'true' },
  })
}

const toggleAccountActiveInline = async () => {
  try {
    const name = String(accountName)
    await toggleAccountStatusClient(name)
    if (accountData.value) {
      accountData.value.is_active = !accountData.value.is_active
    }
  } catch (e) {
    console.error('Failed to toggle account active state', e)
  }
}

// Search model for Account Products table
const productSearchAccount = ref('')

const redirectToDashboardUser = (juid: string, dashboardUrl: string) => {
  const userStore = useUserStore()

  const baseUrl = dashboardUrl.replace(/\/+$/, '')
  window.open(
    `${baseUrl}/?token=${userStore?.token}&juid=${userStore.juid}&role=${userStore?.role}&redirectionDo=true`,
    '_blank'
  )
}

// Row click handler to open dashboard URL
const getParentPathFrom = (path: string) => {
  const idx = String(path || '').lastIndexOf('/')
  return idx > 0 ? String(path || '').slice(0, idx) : ''
}
const isParentDisabled = (raw: any) => {
  const p = getParentPathFrom(String(raw?.path || ''))
  if (!p) return false
  const parent = (accountProducts.value || []).find(
    (x: any) => String(x?.path || '') === p || String(x?.productName || '') === p.split('/').pop()
  )
  if (!parent) return false
  const isActive = (parent?.isActive ?? true) && (parent?.enFlag ?? true)
  return !isActive
}

const onRowClick = (_event: any, row: any) => {
  const item = row?.item ?? row
  const raw = item?.raw ?? item
  if (isParentDisabled(raw)) return
  if (!raw?.dashboard) return
  const canOpen = (raw?.isActive ?? true) && (raw?.enFlag ?? true)
  if (canOpen) {
    redirectToDashboardUser(accountData?.value?.juid, raw.dashboard)
  }
}

// Focus search after table data loads
const productSearchRef = ref()
watch(loadingProd, (val, oldVal) => {
  if (oldVal && !val) {
    setTimeout(() => {
      try {
        productSearchRef.value?.focus?.()
      } catch {}
    }, 0)
  }
})
</script>

<template>
  <v-container>
    <v-row
      id="products"
      v-if="showProductsSection"
      class="table-box"
      :class="role === 'user' ? 'd-none' : ''"
    >
      <v-col cols="12">
        <div style="display: flex !important; justify-content: space-between; flex-wrap: wrap">
          <div class="text-h5 font-weight-medium mb-10">Available Products</div>
        </div>

        <v-data-table
          data-testid="available-products-table"
          :items-per-page="50"
          :headers="headers"
          :items="
            accountProducts.filter((p) => {
              const q = (productSearchAccount || '').toLowerCase()
              if (!q) return true
              const fn = (p?.productFriendlyName || '').toLowerCase()
              const pn = (p?.productName || '').toLowerCase()
              return fn.includes(q) || pn.includes(q)
            })
          "
          :loading="isLoading || loadingProd"
          item-value="productName"
          class="elevation-1 striped-rows"
          dense
          style="max-width: 100%; width: 100%; overflow-x: hidden"
        >
          <template #no-data>
            <div class="text-center py-6 text-grey-darken-1">No available products found.</div>
          </template>
          <template #top>
            <!-- Desktop: inline compact search on the right -->
            <div
              v-if="!$vuetify.display.mdAndDown"
              class="bg-grey-lighten-5 d-flex align-center pb-2"
              style="width: 100%; gap: 8px; padding: 8px 8px 0 8px"
            >
              <v-spacer></v-spacer>
              <v-text-field
                ref="productSearchRef"
                v-model="productSearchAccount"
                data-testid="product-search-input"
                placeholder="Search products…"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="compact"
                variant="solo"
                class="search-in-bar mr-2"
                style="max-width: 380px"
              ></v-text-field>
            </div>
            <!-- Mobile: full-width search on its own row -->
            <div v-else class="bg-grey-lighten-5 pb-2" style="width: 100%; padding: 8px 8px 0 8px">
              <v-text-field
                ref="productSearchRef"
                v-model="productSearchAccount"
                data-testid="product-search-input"
                placeholder="Search products…"
                clearable
                prepend-inner-icon="mdi-magnify"
                hide-details
                density="comfortable"
                variant="solo"
                style="width: 100%; max-width: 100%"
              ></v-text-field>
            </div>
          </template>
          <template #item.status="{ item }">
            <v-chip
              size="x-small"
              :color="(item?.isActive ?? true) && (item?.enFlag ?? true) ? 'green' : 'red'"
              variant="tonal"
            >
              {{ (item?.isActive ?? true) && (item?.enFlag ?? true) ? 'Active' : 'Inactive' }}
            </v-chip>
          </template>
          <template #item.actions="{ item }">
            <div class="d-flex justify-end">
              <v-btn
                size="small"
                variant="flat"
                color="primary"
                prepend-icon="mdi-open-in-new"
                :disabled="!((item?.isActive ?? true) && (item?.enFlag ?? true) && item?.dashboard)"
                @click.stop="
                  (item?.isActive ?? true) &&
                  (item?.enFlag ?? true) &&
                  item?.dashboard &&
                  redirectToDashboardUser(accountData?.juid, item?.dashboard)
                "
              >
                Open Dashboard
              </v-btn>
            </div>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <h4 v-if="showProductsSection" id="products-title" :class="role === 'user' ? 'mt-0' : 'd-none'">
      Account Products
    </h4>

    <v-row
      v-if="showProductsSection"
      class="table-box"
      :class="role === 'user' ? 'mt-4' : 'd-none'"
    >
      <v-data-table
        :headers="headers1"
        :items="accountProducts"
        item-value="productName"
        class="elevation-1 striped-rows even-cols"
        dense
        style="max-width: 100%; width: 100%; overflow-x: hidden"
        @click:row="onRowClick"
      >
        <template #no-data>
          <div class="text-center py-6 text-grey-darken-1">No enrolled products found.</div>
        </template>
      </v-data-table>
    </v-row>
    <v-row v-if="showInfoSection">
      <!-- Account Name Header -->
      <v-col cols="12" id="info" class="subheader-sticky">
        <div style="display: flex !important; justify-content: space-between; flex-wrap: wrap">
          <div class="text-h5 font-weight-medium">Account Information</div>
          <div class="d-flex ga-3 text-copy">
            <v-btn
              data-testid="edit-account-btn"
              :disabled="!accountData?.is_active"
              v-if="userData?.role == 'client_manager' || userData?.role == 'client_admin'"
              variant="text"
              size="small"
              prepend-icon="mdi-pencil"
              class="text-grey-darken-2"
              @click="navigateToEditAccount(accountData?.juid)"
            >
              Edit Account Info
            </v-btn>
          </div>
        </div>
      </v-col>

      <!-- Account Details -->
      <v-col cols="12" md="6">
        <v-card class="accountOverviewCard infoCard">
          <v-card-title>Account Details</v-card-title>
          <v-card-text>
            <dl class="kv">
              <div>
                <dt>Account Name</dt>
                <dd>{{ dash(accountData?.accountName) }}</dd>
              </div>
              <div>
                <dt>Account Name Friendly</dt>
                <dd>{{ dash(accountData?.accountNameFriendly) }}</dd>
              </div>
              <div>
                <dt>Website</dt>
                <dd>
                  <template v-if="asLink(accountData?.mainWebsite)">
                    <a :href="asLink(accountData?.mainWebsite)!" target="_blank" rel="noopener">
                      {{ accountData?.mainWebsite }}
                    </a>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      class="copyBtn"
                      @click="copyToClipboard(asLink(accountData?.mainWebsite)!)"
                    >
                      <v-icon size="16">mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>{{ dash(accountData?.mainWebsite) }}</template>
                </dd>
              </div>
              <div>
                <dt>JUID</dt>
                <dd>
                  {{ dash(accountData?.juid) }}
                  <v-btn
                    icon
                    size="x-small"
                    variant="text"
                    class="copyBtn"
                    @click="copyToClipboard(accountData?.juid)"
                  >
                    <v-icon size="16">mdi-content-copy</v-icon>
                  </v-btn>
                </dd>
              </div>
              <div>
                <dt>Account Id (a5Account)</dt>
                <dd>{{ dash(accountData?.a5Account) }}</dd>
              </div>
              <div>
                <dt>Parent Account</dt>
                <dd>{{ dash(accountData?.parentAccount) }}</dd>
              </div>
              <div>
                <dt>Account Type</dt>
                <dd>{{ dash(accountData?.accountType) }}</dd>
              </div>
              <div>
                <dt>Dealer PA</dt>
                <dd>{{ dash(accountData?.dealerPA) }}</dd>
              </div>
            </dl>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Primary Contact -->
      <v-col cols="12" md="6">
        <v-card class="accountOverviewCard infoCard">
          <v-card-title>Primary Contact</v-card-title>
          <v-card-text>
            <dl class="kv">
              <div>
                <dt>Name</dt>
                <dd>{{ dash(accountData?.primaryContact?.name) }}</dd>
              </div>
              <div>
                <dt>Email</dt>
                <dd>
                  <template v-if="accountData?.primaryContact?.email">
                    <a :href="`mailto:${accountData?.primaryContact?.email}`">
                      {{ accountData?.primaryContact?.email }}
                    </a>
                    <v-btn
                      icon
                      size="x-small"
                      variant="text"
                      class="copyBtn"
                      @click="copyToClipboard(accountData?.primaryContact?.email)"
                    >
                      <v-icon size="16">mdi-content-copy</v-icon>
                    </v-btn>
                  </template>
                  <template v-else>—</template>
                </dd>
              </div>
              <div>
                <dt>Phone</dt>
                <dd>
                  <template v-if="telHref(accountData?.primaryContact?.phone)">
                    <a :href="telHref(accountData?.primaryContact?.phone)!">
                      {{ accountData?.primaryContact?.phone }}
                    </a>
                  </template>
                  <template v-else>{{ dash(accountData?.primaryContact?.phone) }}</template>
                </dd>
              </div>
            </dl>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Billing Info -->
      <v-col cols="12" md="6">
        <v-card class="accountOverviewCard infoCard">
          <v-card-title>Billing Info</v-card-title>
          <v-card-text>
            <dl class="kv">
              <div>
                <dt>Name</dt>
                <dd>{{ dash(accountData?.billing?.name) }}</dd>
              </div>
              <div>
                <dt>Address</dt>
                <dd>
                  {{ dash(accountData?.billing?.address_1) }}
                  <template v-if="accountData?.billing?.address_2">
                    <br />
                    {{ accountData?.billing?.address_2 }}
                  </template>
                </dd>
              </div>
              <div>
                <dt>City</dt>
                <dd>{{ dash(accountData?.billing?.city) }}</dd>
              </div>
              <div>
                <dt>State</dt>
                <dd>{{ dash(accountData?.billing?.state) }}</dd>
              </div>
              <div>
                <dt>Zip</dt>
                <dd>{{ dash(accountData?.billing?.zip) }}</dd>
              </div>
            </dl>
          </v-card-text>
        </v-card>
      </v-col>
      <v-col cols="12" md="6">
        <v-card class="accountOverviewCard infoCard">
          <v-card-title>Regional Information</v-card-title>
          <v-card-text>
            <dl class="kv">
              <div>
                <dt>State Code</dt>
                <dd>{{ dash(accountData?.stateCode) }}</dd>
              </div>
              <div>
                <dt>Country Code</dt>
                <dd>{{ dash(accountData?.countryCode) }}</dd>
              </div>
              <div>
                <dt>Client ID</dt>
                <dd>{{ dash(accountData?.clientId) }}</dd>
              </div>
              <div>
                <dt>Initials</dt>
                <dd>{{ dash(accountData?.initials) }}</dd>
              </div>
              <div>
                <dt>Timezone</dt>
                <dd>{{ dash(accountData?.timezone) }}</dd>
              </div>
            </dl>
          </v-card-text>
        </v-card>
      </v-col>

      <!-- Notes -->
      <v-col cols="12">
        <v-card class="accountOverviewCard infoCard">
          <v-card-title>Notes</v-card-title>
          <v-card-text class="note">{{ accountData?.notes }}</v-card-text>
        </v-card>
      </v-col>
    </v-row>
    <v-spacer></v-spacer>
    <br />
    <br />

    <v-spacer></v-spacer>
    <v-dialog v-model="dialog" max-width="400">
      <v-card>
        <v-card-title>Confirm Action</v-card-title>
        <v-card-text>
          Are you sure you want to {{ selectedAccount?.enabled ? 'enable' : 'disable' }} this
          account?
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" @click="dialog = false">Cancel</v-btn>
          <v-btn color="primary" @click="toggleAccountFunc">Confirm</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<style scoped>
.table-box {
  margin-bottom: 70px;
}

.accountOverviewCard .v-card-title {
  font-size: 1.25rem;
  font-weight: bold;
}

.accountOverviewCard .v-card-text {
  font-size: 1rem;
}

@media (max-width: 600px) {
  .accountOverviewCard {
    margin: 10px;
  }

  .accountOverviewCard .v-card-title {
    font-size: 1.1rem;
  }

  .accountOverviewCard .v-card-text {
    font-size: 0.9rem;
  }
}

.height-loader {
  height: 100vh !important;
  display: flex;
  justify-content: center;
  align-items: center;
}

.image-div {
  width: 60px;
  height: 60px;
  border-radius: 100%;
  overflow: hidden;
}

.image-div > img {
  object-fit: cover;
  width: 100%;
}

::deep(.v-data-table__tr:hover) .row-hover-floater {
  opacity: 1;
}

.row-hover-floater.disabled {
  pointer-events: none;
  opacity: 0.3 !important;
}

.v-data-table {
  max-width: 100%;
  width: 100%;
  overflow-x: auto;
}

::deep(.v-data-table .inactive-row) {
  background-color: rgba(0, 0, 0, 0.774) !important;
}

::deep(.v-data-table .inactive-row > td) {
  color: #999 !important;
  opacity: 0.8;
}

::deep(.v-data-table .inactive-row:hover > td) {
  background-color: rgba(15, 4, 4, 0.887) !important;
}

::deep(.v-data-table .inactive-row .v-data-table__mobile-row:first-child::after) {
  content: ' (Inactive)';
  color: #f44336;
  font-size: 0.8em;
}

::deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

::deep(.striped-rows .v-data-table__tr:nth-child(even)) {
  background-color: #f8f9fa;
}
::deep(.striped-rows .v-data-table__tr:hover) {
  background-color: #e9ecef !important;
}

::deep(.striped-rows .v-data-table__tr) {
  color: inherit;
}
::deep(.v-data-table-header) {
  position: sticky;
  top: 0;
  z-index: 2;
  background-color: rgb(137, 44, 44) !important;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.even-cols :deep(.v-data-table__th),
.even-cols :deep(.v-data-table__td) {
  width: auto;
}
.even-cols :deep(.v-data-table__wrapper table) {
  table-layout: fixed;
}

.even-cols :deep(th),
.even-cols :deep(td) {
  width: calc(100% / var(--even-col-count, 2));
}

.equal-columns :deep(.v-data-table__th),
.equal-columns :deep(.v-data-table__td) {
  width: 50%;
}

.status-text {
  font-weight: 600;
}
.status-active {
  color: #2e7d32;
}
.status-inactive {
  color: #c62828;
}

.infoCard .v-card-title {
  font-size: 1.05rem;
  font-weight: 600;
}

.kv {
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;
}
.kv > div {
  display: grid;
  grid-template-columns: 180px 1fr;
  gap: 12px;
  align-items: start;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}
.kv > div:last-child {
  border-bottom: 0;
}

.kv dt {
  color: #6b7280; /* gray-500 */
  font-weight: 600;
  font-size: 0.92rem;
}
.kv dd {
  margin: 0;
  font-size: 0.95rem;
  word-break: break-word;
}

.copyBtn {
  margin-left: 6px;
  opacity: 0.6;
}
.copyBtn:hover {
  opacity: 1;
}

.note {
  white-space: pre-wrap;
  line-height: 1.4;
}

/* stack nicely on small screens */
@media (max-width: 720px) {
  .kv > div {
    grid-template-columns: 1fr;
    gap: 4px;
  }
  .kv dt {
    font-size: 0.9rem;
  }
  .kv dd {
    font-size: 0.95rem;
  }
}

.subheader-sticky {
  position: sticky;
  top: 56px; /* below app bar and account header */
  z-index: 3;
  background: white;
}

@media (max-width: 960px) {
  .subheader-sticky {
    top: 56px;
  }
}
</style>
